/**
 * What a table's columns MEAN (Olivia's 2g, document themes: "the table owns
 * meaning, the paper owns the look"): decimal alignment, a unit in the header,
 * and the best value in bold. They travel with the table into every paper and
 * into Copy LaTeX.
 *
 * Stored in the table content beside `columnAlign`, one entry per column:
 *   - `columnAlign[i] = "decimal"` — an siunitx S column;
 *   - `columnUnit[i]`              — the header prints `Top-1 (%)`, and the
 *                                    unit is stripped from cells that repeat it;
 *   - `columnBest[i]`              — "higher" | "lower": the best number is bold.
 *
 * Derived, never stored: which cells are best, the S column's table-format, and
 * AUTOMATIC decimal alignment — a column whose alignment the author has not
 * chosen (absent, empty, "auto", or the default "l" every untouched column
 * stores) and whose body holds at least two numbers and nothing but numbers
 * (blank cells and dashes aside). Centre and right are choices and win. Merged
 * cells turn the derived options off for the table: a span moves cells between
 * columns, and a wrong guess about which numbers line up is worse than none.
 *
 * The server mirrors this in `Lilia.Engines/TableColumnFormat.cs`; change both
 * together.
 */
import { cellText, isSpanned, type Cell } from "./tableCells";

export type Best = "higher" | "lower";

export interface ParsedNumber {
  /** "-", "+" (written by the author) or "". */
  sign: "" | "-" | "+";
  /** Integer digits, separators removed; "" for `.5`. */
  int: string;
  /** The integer part as written (keeps `1,204`'s comma). */
  intWritten: string;
  frac: string;
  hasPoint: boolean;
  grouped: boolean;
  percent: boolean;
  /** The author wrapped the whole cell in \textbf. */
  bold: boolean;
  value: number;
}

const NUMBER = /^([+\-−–])?\s*(\d{1,3}(?:,\d{3})+|\d+)?(?:\.(\d+))?\s*(%)?$/;
const WHOLLY_BOLD = /^\\textbf\{([\s\S]*)\}$/;
const WHOLLY_MATHS = /^\$([^$]*)\$$/;

/** A cell that holds no value: empty, or a dash standing for one. */
export function isBlank(raw: string | null | undefined): boolean {
  const t = (raw ?? "").trim();
  return t === "" || t === "-" || t === "--" || t === "---" || t === "–" || t === "—";
}

/** `76.1`, `−1.4`, `+2.7`, `1,204`, `50.7%`, `$+2.7$`, `\textbf{94.8}`; null for anything else. */
export function parseNumber(raw: string | null | undefined): ParsedNumber | null {
  let t = (raw ?? "").trim();
  let bold = false;
  const b = t.match(WHOLLY_BOLD);
  if (b && !b[1].includes("}")) {
    bold = true;
    t = b[1].trim();
  }
  const m0 = t.match(WHOLLY_MATHS);
  if (m0) t = m0[1].trim();
  if (!t) return null;
  const m = t.match(NUMBER);
  if (!m) return null;
  const intRaw = m[2] ?? "";
  const frac = m[3] ?? "";
  if (!intRaw && !frac) return null;
  const sign: ParsedNumber["sign"] = !m[1] ? "" : m[1] === "+" ? "+" : "-";
  const int = intRaw.replace(/,/g, "");
  let value = Number(`${int || "0"}${frac ? `.${frac}` : ""}`);
  if (sign === "-") value = -value;
  return { sign, int, intWritten: intRaw, frac, hasPoint: t.includes("."), grouped: intRaw.includes(","), percent: !!m[4], bold, value };
}

/** The cell without a trailing unit, when what is left is a number (`12 ms` → `12`; `items` stays). */
export function stripUnit(raw: string, unit: string | null | undefined): string {
  if (!unit) return raw;
  const t = raw.trim();
  if (!t.endsWith(unit)) return raw;
  const rest = t.slice(0, t.length - unit.length).trimEnd();
  return rest && parseNumber(rest) ? rest : raw;
}

/** `Top-1` + `%` → `Top-1 (%)`; a header already ending in `(%)` or `[%]` is left alone. */
export function headerWithUnit(header: string, unit: string | null | undefined): string {
  if (!unit) return header;
  const h = header.trimEnd();
  if (h.endsWith(`(${unit})`) || h.endsWith(`[${unit}]`)) return header;
  return h ? `${h} (${unit})` : `(${unit})`;
}

/** A unit: a percent sign, or a short run of letters (ms, M, GB, °C, µs, ×). */
const UNIT = /^(?:%|[A-Za-zµμ°Ω×]{1,5}(?:\/[A-Za-z]{1,3})?)$/;

/**
 * The unit every non-blank body cell repeats, when there is one: `12 ms`,
 * `9 ms` → `ms`; `45.2%`, `50%` → `%`. Two cells at least, and the rest of each
 * cell must be a number. This is the column menu's *Unit* offer.
 */
export function detectUnit(cells: readonly string[]): string | null {
  const vals = cells.map((c) => (c ?? "").trim()).filter((c) => !isBlank(c));
  if (vals.length < 2) return null;
  let unit: string | null = null;
  for (const v of vals) {
    const m = v.match(/^(.*?\d)\s*([^\d\s.,+\-−].*)$/);
    if (!m || !UNIT.test(m[2]) || !parseNumber(m[1])) return null;
    if (unit === null) unit = m[2];
    else if (unit !== m[2]) return null;
  }
  return unit;
}

export type ColumnAlign = "l" | "c" | "r" | "decimal";

export interface ColumnPlan {
  index: number;
  /** An siunitx S column (decimal-aligned). */
  decimal: boolean;
  /** Decimal because every value is a number, not because the author chose it. */
  automatic: boolean;
  unit: string | null;
  best: Best | null;
  /** The best value; every cell holding it is bold (a tie bolds them all). */
  bestValue: number | null;
  /** The body's numbers (empty in a table with merged cells). */
  numbers: ParsedNumber[];
  /** Widest integer part and fraction part as displayed, for HTML decimal alignment. */
  intWidth: number;
  fracWidth: number;
}

/** What the planner needs from a table; `TableData` has it. */
export interface PlannableTable {
  hasHeader: boolean;
  align: readonly (string | null | undefined)[];
  header: readonly Cell[];
  rows: readonly (readonly Cell[])[];
  units?: readonly (string | null | undefined)[];
  best?: readonly (Best | null | undefined)[];
}

const AUTO = new Set(["", "l", "left", "auto"]);
const DECIMAL = new Set(["decimal", "d", "s"]);

export function isDecimalAlign(a: string | null | undefined): boolean {
  return DECIMAL.has((a ?? "").toLowerCase());
}

/** The integer and fraction parts as a reader sees them: `−1`, `.25%`. */
export function displayParts(n: ParsedNumber): { int: string; frac: string } {
  const sign = n.sign === "-" ? "−" : n.sign;
  return {
    int: sign + (n.grouped ? n.intWritten : n.int || (n.hasPoint ? "0" : "")),
    frac: (n.hasPoint ? `.${n.frac}` : "") + (n.percent ? "%" : ""),
  };
}

/** Plan every column of the table. `width` is the column count to plan for. */
export function planColumns(t: PlannableTable, width: number): ColumnPlan[] {
  const hasSpans = t.rows.some((r) => r.some(isSpanned)) || t.header.some(isSpanned);
  return Array.from({ length: Math.max(0, width) }, (_, c) => {
    const align = String(t.align[c] ?? "").trim().toLowerCase();
    const explicitDecimal = DECIMAL.has(align);
    const auto = AUTO.has(align);
    const unitRaw = (t.units?.[c] ?? "").trim();
    const unit = t.hasHeader && unitRaw ? unitRaw : null;
    const best = t.best?.[c] === "higher" || t.best?.[c] === "lower" ? (t.best[c] as Best) : null;

    const numbers: ParsedNumber[] = [];
    let others = 0;
    for (const row of t.rows) {
      const cell = row[c];
      if (!cell) continue;
      const text = stripUnit(cellText(cell), unit);
      if (isBlank(text)) continue;
      const n = isSpanned(cell) ? null : parseNumber(text);
      if (n) numbers.push(n);
      else others++;
    }

    const decimal = explicitDecimal || (auto && !hasSpans && others === 0 && numbers.length >= 2);

    let bestValue: number | null = null;
    if (best && !hasSpans && numbers.length >= 2) {
      const values = numbers.map((n) => n.value);
      const target = best === "higher" ? Math.max(...values) : Math.min(...values);
      if (values.some((v) => v !== target)) bestValue = target;
    }

    const kept = hasSpans ? [] : numbers;
    const parts = kept.map(displayParts);
    return {
      index: c,
      decimal,
      automatic: decimal && !explicitDecimal,
      unit,
      best,
      bestValue,
      numbers: kept,
      intWidth: Math.max(1, ...parts.map((p) => p.int.length)),
      fracWidth: Math.max(0, ...parts.map((p) => p.frac.length)),
    };
  });
}

/** Whether this body text is its column's best value. */
export function isBestText(plan: ColumnPlan | undefined, bodyText: string): boolean {
  if (!plan || plan.bestValue === null) return false;
  const n = parseNumber(bodyText);
  return !!n && n.value === plan.bestValue;
}

/** `S[table-format=-2.2, mode=text, …]` — the same string the server writes. */
export function sColumnSpec(plan: ColumnPlan): string {
  const opts: string[] = [];
  const ns = plan.numbers;
  if (ns.length) {
    const ints = Math.max(...ns.map((n) => Math.max(1, n.int.length)));
    const fracs = Math.max(...ns.map((n) => n.frac.length));
    const sign = ns.some((n) => n.sign) ? "-" : "";
    opts.push(`table-format=${sign}${ints}${fracs > 0 ? `.${fracs}` : ""}`);
  }
  opts.push("mode=text");
  if (ns.some((n) => n.sign === "+")) opts.push("retain-explicit-plus");
  if (ns.some((n) => n.grouped)) opts.push("group-digits=integer, group-separator={,}, group-minimum-digits=4");
  else if (ns.some((n) => n.int.length >= 5 || n.frac.length >= 5)) opts.push("group-digits=none");
  if (plan.bestValue !== null || ns.some((n) => n.bold)) opts.push("reset-text-series=false");
  return `S[${opts.join(", ")}]`;
}

/** A number as an S column reads it, `\bfseries` in front when bold. */
export function numberLatex(n: ParsedNumber, bold: boolean): string {
  return `${bold ? "\\bfseries " : ""}${n.sign}${n.int}${n.hasPoint ? `.${n.frac}` : ""}${n.percent ? "{\\%}" : ""}`;
}

/** LaTeX's specials in a unit (the header is source; `%` would start a comment). */
export function escapeUnit(unit: string): string {
  return unit.replace(/[\\%&#_$]/g, (ch) => (ch === "\\" ? "\\textbackslash{}" : `\\${ch}`));
}
